import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  useTheme,
  useMediaQuery,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import Loader from 'components/Loader';
import { getProducts } from 'services/ErplyAPI';
import {
  getCurrencyFormatter,
  getShowPricesWithTax,
} from 'reducers/configs/settings';
import { PluginComponent } from 'plugins';
import { Product } from 'types/Product';

import StockTableDesktop from './StockTableDesktop';
import StockTableMobile from './StockTableMobile';

interface Props {
  productItem?: Product;
}

function ProductInformation({ productItem }: Props) {
  const { t } = useTranslation('product');
  const format = useSelector(getCurrencyFormatter);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('xs'));

  const {
    value: override,
    error,
    loading,
  } = useAsync<Product | null>(async () => {
    if (!productItem || override) return null;
    const response = await getProducts({
      productID: productItem.productID,
      from: 0,
      count: 1,
      getStockInfo: 1,
    });
    return response.records[0] ?? null;
  }, [productItem]);

  const shouldUsePriceWithVAT = useSelector(getShowPricesWithTax);

  return (
    <div data-testid="product-info">
      <Typography
        variant="h6"
        style={{ marginTop: '1rem', marginBottom: '0.5rem' }}
      >
        {t('productView.title')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableBody>
            <TableRow>
              <TableCell>{t('relatedProducts.headers.name')}</TableCell>
              <TableCell data-testid="name">{productItem?.name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('relatedProducts.headers.code')}</TableCell>
              <TableCell data-testid="code">{productItem?.code}</TableCell>
            </TableRow>
            {productItem?.code2 && (
              <TableRow>
                <TableCell>{t('relatedProducts.headers.ean')}</TableCell>
                <TableCell data-testid="code2">{productItem?.code2}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell>{t('relatedProducts.headers.price')}</TableCell>
              <TableCell data-testid="price">
                {format(
                  shouldUsePriceWithVAT
                    ? productItem?.priceWithVat
                    : productItem?.price,
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t('relatedProducts.headers.priceListPrice')}
              </TableCell>
              <TableCell data-testid="price-list-price">
                {format(
                  shouldUsePriceWithVAT
                    ? productItem?.priceListPriceWithVat
                    : productItem?.priceListPrice,
                )}
              </TableCell>
            </TableRow>
            {productItem?.groupName && (
              <TableRow>
                <TableCell>{t('relatedProducts.headers.group')}</TableCell>
                <TableCell data-testid="group-name">
                  {productItem?.groupName}
                </TableCell>
              </TableRow>
            )}
            {productItem?.categoryName && (
              <TableRow>
                <TableCell>{t('relatedProducts.headers.category')}</TableCell>
                <TableCell data-testid="category">
                  {productItem?.categoryName}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PluginComponent hookname="UIStockDataTable">
        <Typography
          variant="h6"
          style={{ marginTop: '1rem', marginBottom: '0.5rem' }}
        >
          {t('relatedProducts.headers.stockInfo.title')}
        </Typography>
        {!error ? (
          <Loader show={loading} block>
            <TableContainer component={Paper} variant="outlined">
              {isMobileView ? (
                <StockTableMobile productInWarehouse={override?.warehouses} />
              ) : (
                <StockTableDesktop productInWarehouse={override?.warehouses} />
              )}
            </TableContainer>
          </Loader>
        ) : (
          <Alert severity="error">
            {t('relatedProducts.stockInfo.failedToFetch')}
          </Alert>
        )}
      </PluginComponent>
    </div>
  );
}

export default ProductInformation;
